import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  OutlinedInput, 
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CalendarToday, 
  LocationOn, 
  Business, 
  AttachMoney,
  ArrowForward,
  Event
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchEventStatusCounts } from '../services/analytics';
import { getAllEventsAdmin } from '../services/api';
import useDebounce from '../hooks/useDebounce';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const eventTypeOptions = [
  { value: '', label: 'All Event Types' },
  { value: 'College_fests', label: 'College Fests' },
  { value: 'conference', label: 'Conference' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'competition', label: 'Competition' },
  { value: 'exhibition', label: 'Exhibition' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'other', label: 'Other' },
];

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'ideathon', label: 'Ideathon' },
  { value: 'hackathon', label: 'Hackathon' },
  { value: 'coding_contests', label: 'Coding Contests' },
  { value: 'design', label: 'Design' },
  { value: 'case_study_competitions', label: 'Case Study Competitions' },
  { value: 'technology', label: 'Technology' },
  { value: 'business', label: 'Business' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'environment', label: 'Environment' },
  { value: 'social', label: 'Social' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'sports', label: 'Sports' },
  { value: 'other', label: 'Other' },
];

const countryOptions = [
  { value: '', label: 'All Countries' },
  { value: 'India', label: 'India' },
  { value: 'International', label: 'International' },
];

const pricingOptions = [
  { value: '', label: 'All' },
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
  { value: 'donation', label: 'Donation' },
];

const createdDateOptions = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Created Today' },
  { value: '2days', label: 'Created in Last 2 Days' },
  { value: '7days', label: 'Created in Last 7 Days' },
];

const EventAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusCounts, setStatusCounts] = useState<any>({});
  const [filters, setFilters] = useState({
    status: [],
    eventType: '',
    category: '',
    country: '',
    state: '',
    pricing: '',
    search: '',
    organization: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    dateRange: '',
    active: '',
    showExpired: false,
    createdDateFilter: '',
  });
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    status: [],
    eventType: '',
    category: '',
    country: '',
    state: '',
    pricing: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    dateRange: '',
    active: '',
    showExpired: false,
    createdDateFilter: '',
  });

  // Debounced search fields
  const debouncedSearch = useDebounce(filters.search, 400);
  const debouncedOrganization = useDebounce(filters.organization, 400);

  useEffect(() => {
    async function loadStatusCounts() {
      try {
        const counts = await fetchEventStatusCounts();
        setStatusCounts(counts);
      } catch (error) {
        console.error('Error loading status counts:', error);
      }
    }
    loadStatusCounts();
  }, []);

  useEffect(() => {
    async function loadEvents() {
      setLoading(true);
      setError(null);
      try {
        const apiFilters = {
          search: debouncedSearch.trim(),
          organization: debouncedOrganization.trim(),
          status: appliedFilters.status.length > 0 ? appliedFilters.status.join(',') : undefined,
          eventType: appliedFilters.eventType,
          category: appliedFilters.category,
          country: appliedFilters.country,
          state: appliedFilters.state,
          pricing: appliedFilters.pricing,
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          registrationStartDate: appliedFilters.registrationStartDate,
          registrationEndDate: appliedFilters.registrationEndDate,
          dateRange: appliedFilters.dateRange,
          active: appliedFilters.active,
          showExpired: appliedFilters.showExpired,
          createdDateFilter: appliedFilters.createdDateFilter,
        };
        const data = await getAllEventsAdmin(apiFilters);
        setEvents(data.data || []);
        setLoading(false);
      } catch (error) {
        setError('Failed to load events');
        setEvents([]);
        setLoading(false);
      }
    }
    loadEvents();
  }, [debouncedSearch, debouncedOrganization, appliedFilters]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleCardClick = (event: any) => {
    navigate(`/events/${event._id}`);
  };

  return (
    <Box maxWidth={1200} mx="auto" p={2}>
      <Typography variant="h4" fontWeight={700} color="primary" mb={3}>
        Event Analytics
      </Typography>
      
      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Total</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.total ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Pending</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.pending ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Accepted</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.accepted ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Rejected</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.rejected ?? 0}
          </Typography>
        </Paper>
      </Box>
      
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Event Type</InputLabel>
            <Select
              value={filters.eventType}
              onChange={e => handleFilterChange('eventType', e.target.value)}
              label="Event Type"
            >
              {eventTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              onChange={e => handleFilterChange('category', e.target.value)}
              label="Category"
            >
              {categoryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={filters.country}
              onChange={e => handleFilterChange('country', e.target.value)}
              label="Country"
            >
              {countryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="State"
            value={filters.state}
            onChange={e => handleFilterChange('state', e.target.value)}
            fullWidth
          />
          
          <FormControl fullWidth>
            <InputLabel>Pricing</InputLabel>
            <Select
              value={filters.pricing}
              onChange={e => handleFilterChange('pricing', e.target.value)}
              label="Pricing"
            >
              {pricingOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Search by Name"
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            fullWidth
          />
          
          <TextField
            label="Search by Organization"
            value={filters.organization}
            onChange={e => handleFilterChange('organization', e.target.value)}
            fullWidth
          />
          
          <TextField
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={e => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={e => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="Registration Start Date"
            type="date"
            value={filters.registrationStartDate}
            onChange={e => handleFilterChange('registrationStartDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="Registration End Date"
            type="date"
            value={filters.registrationEndDate}
            onChange={e => handleFilterChange('registrationEndDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <FormControl fullWidth>
            <InputLabel>Show by Created Date</InputLabel>
            <Select
              value={filters.createdDateFilter}
              onChange={e => handleFilterChange('createdDateFilter', e.target.value)}
              label="Show by Created Date"
            >
              {createdDateOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <FormControl>
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                id="showExpired"
                checked={filters.showExpired}
                onChange={e => handleFilterChange('showExpired', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <label htmlFor="showExpired" style={{ cursor: 'pointer' }}>
                <Typography variant="body2">Show expired events (based on registration end date)</Typography>
              </label>
            </Box>
          </FormControl>
        </Box>
        
        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button 
            variant="outlined" 
            onClick={() => setFilters({
              status: [],
              eventType: '',
              category: '',
              country: '',
              state: '',
              pricing: '',
              search: '',
              organization: '',
              startDate: '',
              endDate: '',
              registrationStartDate: '',
              registrationEndDate: '',
              dateRange: '',
              active: '',
              showExpired: false,
              createdDateFilter: '',
            })}
          >
            Clear Filters
          </Button>
          <Button variant="contained" color="primary" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </Box>
      </Paper>
      
      {/* Events Cards */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          All Events
        </Typography>
        
        {loading ? (
          <Typography>Loading events...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            {!Array.isArray(events) ? (
              <Typography>Invalid data format received</Typography>
            ) : events.length === 0 ? (
              <Typography>No events found.</Typography>
            ) : (
              <Box 
                display="grid" 
                gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" 
                gap={3}
              >
                {Array.isArray(events) && events.map((event, index) => (
                  <Box key={event._id || event.id || index}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        }
                      }}
                      onClick={() => handleCardClick(event)}
                    >
                      <Box sx={{ position: 'relative', height: 140, width: '100%' }}>
                        {event.images?.banner || event.banner ? (
                          <CardMedia
                            component="img"
                            height="140"
                            image={event.images?.banner || event.banner}
                            alt={event.title || 'Event'}
                            sx={{
                              objectFit: 'cover',
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'grey.200',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = '';
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              minHeight: 140,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              background: 'linear-gradient(135deg, #5b7fff 0%, #6ec6ff 100%)',
                            }}
                          >
                            <Event sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                            <Typography variant="subtitle1" color="white" fontWeight={700} align="center" sx={{ px: 2, textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                              {event.title || 'Event'}
                            </Typography>
                          </Box>
                        )}
                        {/* Featured Chip - top left, yellow */}
                        {event.isFeatured && (
                          <Chip
                            label="Featured"
                            color="warning"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              left: 10,
                              zIndex: 2,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              backgroundColor: '#ffe066',
                              color: '#7a5c00',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          />
                        )}
                        {/* Status Chip - top right, bold, white text, solid bg */}
                        <Chip
                          label={event.status || 'N/A'}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            px: 1.5,
                            color: '#fff',
                            backgroundColor:
                              event.status === 'published'
                                ? '#43a047'
                                : event.status === 'draft'
                                ? '#fbc02d'
                                : event.status === 'ongoing'
                                ? '#2196f3'
                                : event.status === 'completed'
                                ? '#9c27b0'
                                : event.status === 'cancelled'
                                ? '#e53935'
                                : '#607d8b',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                            textTransform: 'capitalize',
                            letterSpacing: 0.5,
                          }}
                        />
                      </Box>

                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        {/* Header */}
                        <Box mb={2}>
                          <Typography variant="h6" component="h3" fontWeight={600} sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2
                          }}>
                            {event.title || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Organization */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <Business sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {event.organization?.name || event.organization || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Event Type & Category */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <Event sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.eventType || 'N/A'} • {event.category || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Location */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {event.location?.city || 'N/A'}, {event.location?.country || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Dates */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.dates?.startDate ? new Date(event.dates.startDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>

                        {/* Pricing */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <AttachMoney sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.pricing?.type === 'free' ? 'Free' : 
                             event.pricing?.type === 'paid' ? `₹${event.pricing?.amount || 0}` :
                             event.pricing?.type === 'donation' ? 'Donation' : 'N/A'}
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Button 
                          size="small" 
                          endIcon={<ArrowForward />}
                          sx={{ ml: 'auto' }}
                        >
                          View Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EventAnalyticsPage; 